import {
  SuperDispatchPriceTrend,
  SuperDispatchTrendPoint,
  SuperDispatchVehicleTypePriceTrend,
} from 'lib/api/getQuickQuote';
import { autoVehicleTypeLabel } from 'lib/autoTransport/autoVehicleTypes';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';

export type { SuperDispatchVehicleTypePriceTrend };

export type SuperDispatchTrendChartRow = {
  date: string;
  index: number;
  [seriesKey: string]: Maybe<number> | string;
};

export type SuperDispatchTrendChartSeries = {
  vehicleType: string;
  key: string;
  label: string;
};

export type SuperDispatchTrendChartBuildResult = {
  rows: SuperDispatchTrendChartRow[];
  series: SuperDispatchTrendChartSeries[];
  pointLookup: ReadonlyMap<
    string,
    ReadonlyMap<string, SuperDispatchTrendPoint>
  >;
};

const LEGACY_TREND_SERIES_KEY = 'legacy_price';

function trendSeriesKey(vehicleType: string): string {
  return vehicleType.trim().toLowerCase().replace(/\s+/g, '_');
}

function coerceTrendPrice(value: unknown): Maybe<number> {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return null;
}

function normalizeTrendPoint(entry: unknown): Maybe<SuperDispatchTrendPoint> {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const record = entry as Record<string, unknown>;
  const date = typeof record.date === 'string' ? record.date : null;
  const price = coerceTrendPrice(record.price);
  if (!date?.trim() || price == null) {
    return null;
  }

  const minPrice = coerceTrendPrice(record.minPrice ?? record.min_price);
  const maxPrice = coerceTrendPrice(record.maxPrice ?? record.max_price);

  return {
    date,
    price,
    ...(minPrice != null && { minPrice }),
    ...(maxPrice != null && { maxPrice }),
  };
}

function normalizedTrendPoints(
  points: SuperDispatchTrendPoint[]
): SuperDispatchTrendPoint[] {
  return points
    .map((point) => normalizeTrendPoint(point))
    .filter(
      (point): point is SuperDispatchTrendPoint =>
        point != null && point.price > 0
    )
    .sort((left, right) => left.date.localeCompare(right.date));
}

// Keep a non-empty trend when postings return but price-trends failed.
export function mergeSuperDispatchPriceTrend(
  existing: MaybeUndef<SuperDispatchPriceTrend>,
  incoming: MaybeUndef<SuperDispatchPriceTrend>
): MaybeUndef<SuperDispatchPriceTrend> {
  if (incoming === undefined) {
    return existing;
  }

  if (incoming?.length) {
    return incoming;
  }

  if (existing?.length) {
    return existing;
  }

  return incoming;
}

function readVehicleType(
  entry: SuperDispatchVehicleTypePriceTrend | SuperDispatchTrendPoint
): Maybe<string> {
  const record = entry as SuperDispatchVehicleTypePriceTrend & {
    vehicle_type?: string;
  };
  const vehicleType = record.vehicleType ?? record.vehicle_type;
  if (!vehicleType?.trim()) {
    return null;
  }

  return vehicleType.trim();
}

function isFlatTrendPoint(entry: unknown): boolean {
  if (!entry || typeof entry !== 'object') {
    return false;
  }

  const record = entry as Record<string, unknown>;
  const date = typeof record.date === 'string' ? record.date : null;
  const price = coerceTrendPrice(record.price);

  return (
    date != null &&
    price != null &&
    readVehicleType(entry as SuperDispatchTrendPoint) == null
  );
}

function isVehicleTypePriceTrendSeries(
  priceTrend: SuperDispatchVehicleTypePriceTrend[] | SuperDispatchTrendPoint[]
): boolean {
  return priceTrend.every((entry) => {
    const record = entry as SuperDispatchVehicleTypePriceTrend & {
      vehicle_type?: string;
    };
    return readVehicleType(entry) != null && Array.isArray(record.points);
  });
}

// Accepts per-type series from the API, or a legacy flat point list.
export function normalizeSuperDispatchPriceTrend(
  priceTrend: Maybe<SuperDispatchPriceTrend>
): SuperDispatchVehicleTypePriceTrend[] {
  if (!priceTrend?.length) {
    return [];
  }

  if (isVehicleTypePriceTrendSeries(priceTrend)) {
    return priceTrend
      .map((entry) => {
        const points = (
          (entry as SuperDispatchVehicleTypePriceTrend).points ?? []
        )
          .map((point) => normalizeTrendPoint(point))
          .filter((point): point is SuperDispatchTrendPoint => point != null);

        return {
          vehicleType: readVehicleType(entry) ?? 'vehicle',
          points,
        };
      })
      .filter((entry) => entry.points.length > 0);
  }

  const flatPoints = priceTrend
    .filter(isFlatTrendPoint)
    .map((entry) => normalizeTrendPoint(entry))
    .filter((point): point is SuperDispatchTrendPoint => point != null);

  if (flatPoints.length > 0) {
    return [
      {
        vehicleType: LEGACY_TREND_SERIES_KEY,
        points: flatPoints,
      },
    ];
  }

  return [];
}

export function hasSuperDispatchPriceTrendChart(
  priceTrend: Maybe<SuperDispatchPriceTrend>
): boolean {
  const series = normalizeSuperDispatchPriceTrend(priceTrend);
  return series.some((item) =>
    normalizedTrendPoints(item.points).some((point) => point.price > 0)
  );
}

export function buildSuperDispatchTrendChartData(
  priceTrend: Maybe<SuperDispatchPriceTrend>
): SuperDispatchTrendChartBuildResult {
  const normalized = normalizeSuperDispatchPriceTrend(priceTrend);
  const activeSeries = normalized
    .map((item) => ({
      vehicleType: item.vehicleType,
      key: trendSeriesKey(item.vehicleType),
      label:
        item.vehicleType === LEGACY_TREND_SERIES_KEY
          ? 'Price'
          : autoVehicleTypeLabel(item.vehicleType),
      points: normalizedTrendPoints(item.points),
    }))
    .filter((item) => item.points.length > 0);

  const dates = [
    ...new Set(
      activeSeries.flatMap((item) => item.points.map((point) => point.date))
    ),
  ].sort();

  const pointLookup = new Map<string, Map<string, SuperDispatchTrendPoint>>();
  for (const item of activeSeries) {
    const byDate = new Map<string, SuperDispatchTrendPoint>();
    for (const point of item.points) {
      byDate.set(point.date, point);
    }
    pointLookup.set(item.key, byDate);
  }

  const rows: SuperDispatchTrendChartRow[] = dates.map((date, index) => {
    const row: SuperDispatchTrendChartRow = { date, index };
    for (const item of activeSeries) {
      const point = pointLookup.get(item.key)?.get(date);
      row[item.key] = point?.price ?? null;
    }
    return row;
  });

  return {
    rows,
    series: activeSeries.map(({ vehicleType, key, label }) => ({
      vehicleType,
      key,
      label,
    })),
    pointLookup,
  };
}

export function buildSuperDispatchTrendXTickIndexes(
  rowCount: number
): number[] {
  if (rowCount <= 1) {
    return [0];
  }

  if (rowCount <= 6) {
    return Array.from({ length: rowCount }, (_, index) => index);
  }

  const tickCount = 6;
  const indexes: number[] = [];
  for (let step = 0; step < tickCount; step++) {
    indexes.push(Math.round((step / (tickCount - 1)) * (rowCount - 1)));
  }

  return [...new Set(indexes)];
}
